/**
 * One simulation step.
 *
 * `dt` is always exactly TICK_DT (0.05) from the game loop, but it stays a
 * parameter: the offline catch-up and the tests both feed it other values.
 *
 * Rendering never calls this and this never touches the DOM.
 */

import { GENERATORS } from '../data/generators.ts';
import { HEAT_MAX, HEAT_MIN } from '../data/balance.ts';
import {
  bulkCount,
  generatorUnlockKey,
  getModifiers,
  netHeat,
  revealThreshold,
  voidShare,
  tps,
  type Modifiers,
} from './formulas.ts';
import { buyGenerator } from './actions.ts';
import { meltdown } from './prestige.ts';
import type { GameState } from './state.ts';

/** What happened during a step that the presentation layer needs to know. */
export interface TickEvents {
  /** Grey Matter awarded by a meltdown this step, if one fired. */
  meltdown: { gain: import('break_infinity.js').default; auto: boolean } | null;
  /** Generator tiers revealed this step. */
  revealed: string[];
}

export function tick(
  state: GameState,
  dt: number,
  mods: Modifiers = getModifiers(state),
  events?: TickEvents,
): void {
  state.runTimeSec += dt;
  state.stats.totalPlaytimeSec += dt;

  // --- Production ----------------------------------------------------------
  const gain = tps(state, mods).mul(dt);
  if (gain.gt(0)) {
    state.thoughts = state.thoughts.add(gain);
    state.thoughtsThisRun = state.thoughtsThisRun.add(gain);
    state.stats.lifetimeThoughts = state.stats.lifetimeThoughts.add(gain);

    if (state.thoughtsThisRun.gt(state.stats.bestRunThoughts)) {
      state.stats.bestRunThoughts = state.thoughtsThisRun;
    }
  }

  // --- Heat ----------------------------------------------------------------
  // Heat is moved through a single accumulator so other sources can push it
  // too. The Intrusive Thoughts expansion spikes heat from here.
  const heatDelta = netHeat(state, mods) * dt;
  state.heat = clamp(state.heat + heatDelta, HEAT_MIN, HEAT_MAX);
  if (state.heat > state.peakHeatThisRun) state.peakHeatThisRun = state.heat;
  if (state.heat > state.stats.hottestSurvived && state.heat < HEAT_MAX) {
    state.stats.hottestSurvived = state.heat;
  }

  // The void announces itself the moment it takes any ground at all.
  if (state.unlocks.void !== true && voidShare(state, mods) > 0) {
    state.unlocks.void = true;
    events?.revealed.push('void');
  }

  if (state.ventCooldown > 0) {
    state.ventCooldown = Math.max(0, state.ventCooldown - dt);
  }

  // --- Progressive reveal --------------------------------------------------
  for (let i = 0; i < GENERATORS.length; i++) {
    const def = GENERATORS[i];
    if (def === undefined) continue;
    const key = generatorUnlockKey(def);
    if (state.unlocks[key] === true) continue;
    if (state.thoughts.gte(revealThreshold(def))) {
      state.unlocks[key] = true;
      events?.revealed.push(def.id);
    }
  }

  // --- Automation ----------------------------------------------------------
  if (mods.flags.autobuy === true) {
    runAutoBuy(state);
  }

  // --- Meltdown ------------------------------------------------------------
  const forced = state.heat >= HEAT_MAX;
  const auto =
    !forced &&
    mods.flags.automelt === true &&
    state.settings.autoMeltdownEnabled &&
    state.heat >= state.settings.autoMeltdownThreshold;

  if (forced || auto) {
    const result = meltdown(state, mods);
    if (events !== undefined) events.meltdown = { gain: result.gain, auto };
  }
}

/** Buys one unit of every enabled tier that is currently affordable. */
function runAutoBuy(state: GameState): void {
  for (let i = 0; i < GENERATORS.length; i++) {
    const def = GENERATORS[i];
    if (def === undefined) continue;
    if (state.settings.autoBuy[def.id] !== true) continue;
    buyGenerator(state, i, bulkCount(state, i, 1));
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function createTickEvents(): TickEvents {
  return { meltdown: null, revealed: [] };
}
